/* Carti Apps — theme JS
   - Sticky header scroll shadow
   - Mobile drawer toggle
   - IntersectionObserver reveals
   - Auto-update year in footer
   - FAQ exclusive-accordion fallback (older browsers)
   - Form helper: populates hidden fields (UTMs, source, IP), submits to HubSpot Forms API v3
   - Sends HubSpot tracking cookie (hubspotutk) so form submissions link to existing contacts
   - Form submitting/loading state on the submit button
   - GA4 analytics events: form_submit, button_click, scroll_depth (75%)
*/
(function () {
  'use strict';

  // ---------- GA4 event helper ----------
  function track(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = Object.assign({ event: eventName }, params || {});
      window.dataLayer.push(payload);
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params || {});
      }
    } catch (e) { /* never let analytics break the page */ }
  }

  // ---------- Cookie helper ----------
  // Read a single cookie by name. Returns '' if not present.
  function getCookie(name) {
    try {
      var prefix = name + '=';
      var parts = document.cookie ? document.cookie.split(';') : [];
      for (var i = 0; i < parts.length; i++) {
        var c = parts[i].replace(/^\s+/, '');
        if (c.indexOf(prefix) === 0) return decodeURIComponent(c.substring(prefix.length));
      }
    } catch (e) { /* ignore */ }
    return '';
  }

  // ---------- Sticky header ----------
  var header = document.querySelector('.carti-header');
  if (header) {
    var onScroll = function () {
      if (window.scrollY > 8) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---------- Mobile drawer ----------
  var toggle = document.querySelector('.carti-header__toggle');
  var drawer = document.querySelector('.carti-mobile-drawer');
  if (toggle && drawer) {
    toggle.addEventListener('click', function () {
      var open = drawer.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    });
  }

  // ---------- Reveals ----------
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.reveal').forEach(function (el) { io.observe(el); });
  } else {
    document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
  }

  // ---------- Footer year ----------
  document.querySelectorAll('[data-current-year]').forEach(function (el) {
    el.textContent = new Date().getFullYear();
  });

  // ---------- FAQ exclusive-accordion fallback ----------
  (function () {
    var faqItems = document.querySelectorAll('details[name="carti-faq"]');
    if (faqItems.length < 2) return;
    var supportsExclusive = (function () {
      try {
        var a = document.createElement('details');
        var b = document.createElement('details');
        a.name = 'x'; b.name = 'x';
        document.body.appendChild(a); document.body.appendChild(b);
        a.open = true; b.open = true;
        var ok = !(a.open && b.open);
        a.remove(); b.remove();
        return ok;
      } catch (e) { return false; }
    })();
    if (supportsExclusive) return;
    faqItems.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        faqItems.forEach(function (other) {
          if (other !== item && other.open) other.open = false;
        });
      });
    });
  })();

  // ---------- Hidden field population ----------
  function getParam(name) {
    try {
      var url = new URL(window.location.href);
      return url.searchParams.get(name) || '';
    } catch (e) { return ''; }
  }

  var contextValues = {
    form_source: window.location.href,
    utm_source: getParam('utm_source'),
    utm_medium: getParam('utm_medium'),
    utm_campaign: getParam('utm_campaign'),
    ip_address: ''
  };

  function applyContext() {
    document.querySelectorAll('form[data-carti-form] input[data-fill]').forEach(function (input) {
      var key = input.getAttribute('data-fill');
      if (key && contextValues[key] !== undefined && contextValues[key] !== '') {
        input.value = contextValues[key];
      }
    });
  }
  applyContext();

  // Fetch IP lazily (non-blocking, fails silently). Track the in-flight promise
  // so submit handler can await it if the form is submitted before the response.
  var ipPromise = null;
  if (document.querySelector('form[data-carti-form] input[data-fill="ip_address"]')) {
    ipPromise = fetch('https://api.ipify.org?format=json', { method: 'GET' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data.ip) {
          contextValues.ip_address = data.ip;
          applyContext();
        }
      })
      .catch(function () { /* offline / rate limited — leave empty */ });
  }

  // ---------- Form submit handler ----------
  document.querySelectorAll('form[data-carti-form]').forEach(function (form) {
    var submitBtn = form.querySelector('button[type="submit"]');
    var originalBtnLabel = submitBtn ? submitBtn.innerHTML : '';

    function setSubmitting(isSubmitting) {
      if (!submitBtn) return;
      if (isSubmitting) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-submitting');
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.innerHTML = '<span class="carti-spinner" aria-hidden="true"></span><span>Sending…</span>';
      } else {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-submitting');
        submitBtn.removeAttribute('aria-busy');
        submitBtn.innerHTML = originalBtnLabel;
      }
    }

    function performSubmit(form, status) {
      var portalId = form.dataset.hubspotPortal;
      var formId = form.dataset.hubspotForm;
      var formName = form.dataset.formName || 'unknown';
      var formIntent = form.dataset.formIntent || formName;

      // Re-apply context just before submit in case IP arrived after page load
      applyContext();

      var fields = [];
      new FormData(form).forEach(function (val, key) {
        if (val === '' || val == null) return;
        fields.push({ objectTypeId: '0-1', name: key, value: val });
      });

      // Build context. Always include pageUri / pageName. Include hutk
      // (HubSpot tracking cookie) when present so the submission links to
      // an existing tracked visitor. Include ipAddress when we have it.
      var ctx = {
        pageUri: window.location.href,
        pageName: document.title
      };
      var hutk = getCookie('hubspotutk');
      if (hutk) ctx.hutk = hutk;
      if (contextValues.ip_address) ctx.ipAddress = contextValues.ip_address;

      var payload = { fields: fields, context: ctx };

      return fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + portalId + '/' + formId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          if (r.ok) {
            track('form_submit', {
              form_name: formName,
              form_intent: formIntent,
              form_source: window.location.pathname
            });
            if (status) {
              status.textContent = "Thanks. We'll be in touch within 1 business day.";
              status.dataset.state = 'success';
            }
            form.reset();
            applyContext();
            setSubmitting(false);
          } else {
            if (status) {
              status.textContent = 'Something went wrong. Please email hello@cartiapps.com.';
              status.dataset.state = 'error';
            }
            setSubmitting(false);
          }
        })
        .catch(function () {
          if (status) {
            status.textContent = 'Connection problem. Please email hello@cartiapps.com.';
            status.dataset.state = 'error';
          }
          setSubmitting(false);
        });
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var portalId = form.dataset.hubspotPortal;
      var formId = form.dataset.hubspotForm;
      var status = form.querySelector('[data-form-status]');

      if (!portalId || !formId) {
        if (status) {
          status.textContent = 'Thanks. (Preview mode — form ID not configured.)';
          status.dataset.state = 'preview';
        }
        return;
      }

      // Show loading state immediately
      setSubmitting(true);
      if (status) {
        status.textContent = 'Sending…';
        status.dataset.state = 'submitting';
      }

      // If the IP request is still in flight (form submitted very fast),
      // wait up to 800ms for it before submitting so IP is included.
      if (ipPromise && !contextValues.ip_address) {
        var raced = Promise.race([
          ipPromise,
          new Promise(function (resolve) { setTimeout(resolve, 800); })
        ]);
        raced.then(function () { performSubmit(form, status); });
      } else {
        performSubmit(form, status);
      }
    });
  });

  // ---------- Newsletter form feedback ----------
  document.querySelectorAll('form.newsletter-form').forEach(function (form) {
    form.addEventListener('submit', function () {
      var btn = form.querySelector('button[type="submit"]');
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Subscribing…';
      }
    });
  });

  // ---------- button_click event ----------
  document.addEventListener('click', function (ev) {
    var el = ev.target.closest('[data-cta], a.carti-btn--primary, a.btn--primary, a[href="/pages/audit"]');
    if (!el) return;
    var label = el.getAttribute('data-cta')
      || (el.textContent || '').trim().slice(0, 60)
      || 'cta';
    track('button_click', {
      cta_label: label,
      cta_destination: el.getAttribute('href') || '',
      page_path: window.location.pathname
    });
  }, { passive: true });

  // ---------- scroll_depth event (75%) ----------
  (function () {
    var fired = false;
    function checkScroll() {
      if (fired) return;
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = (window.scrollY || doc.scrollTop) / scrollable;
      if (pct >= 0.75) {
        fired = true;
        track('scroll_depth', { percent_scrolled: 75, page_path: window.location.pathname });
        window.removeEventListener('scroll', checkScroll);
      }
    }
    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
  })();
})();
